import React, { useState, useRef, useEffect } from 'react';
import { images } from '../shared/assets';
import { useLanguageOptions } from '../../hooks/useContentful';
import { ContentfulText } from '../Contentful';
import { useLanguage, useTranslation, SUPPORTED_LANGUAGES, LANGUAGE_NAMES, SupportedLanguage } from '../../i18n';
import './LanguageDropdown.css';

interface LanguageOption {
  code: string;
  label: string;
  flag?: string;
  fullName?: string;
}

interface LanguageDropdownProps {
  variant?: 'light' | 'dark' | 'transparent' | 'faded' | 'invert-faded';
  className?: string;
  showFlags?: boolean;
  compact?: boolean;
  horizontal?: boolean;
}

const staticLanguages: LanguageOption[] = SUPPORTED_LANGUAGES.map(lang => ({
  code: lang.toUpperCase(),
  label: LANGUAGE_NAMES[lang as SupportedLanguage],
  flag: images.flags?.[lang as keyof typeof images.flags],
  fullName: LANGUAGE_NAMES[lang as SupportedLanguage]
}));

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({ 
  variant = 'transparent',
  className = '',
  showFlags = true,
  compact = false,
  horizontal = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentLanguage, changeLanguage, isChangingLanguage } = useLanguage();
  const { t } = useTranslation();
  
  // Fetch language options from Contentful
  const { data: languageData } = useLanguageOptions();
  
  // Use static languages for now (can integrate Contentful later if needed)
  const languages = staticLanguages;
  
  // Get the current selected language based on i18n state
  const selectedLanguage = languages.find(lang => 
    lang.code.toLowerCase() === currentLanguage
  ) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Ensure dropdown doesn't go above viewport
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const dropdown = dropdownRef.current.querySelector('.language-dropdown__menu');
      if (dropdown) {
        const rect = dropdown.getBoundingClientRect();
        if (rect.top < 0) {
          (dropdown as HTMLElement).style.top = '100%';
          (dropdown as HTMLElement).style.bottom = 'auto';
        }
      }
    }
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleLanguageSelect = (language: LanguageOption) => {
    setIsOpen(false);
    // Change the actual language using our i18n system
    const langCode = language.code.toLowerCase() as SupportedLanguage;
    if (SUPPORTED_LANGUAGES.includes(langCode)) {
      changeLanguage(langCode);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent, language?: LanguageOption) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (language) {
        handleLanguageSelect(language);
      } else {
        handleToggle();
      }
    }
  };

  const dropdownClasses = [
    'language-dropdown',
    `language-dropdown--${variant}`,
    isOpen ? 'language-dropdown--open' : '',
    compact ? 'language-dropdown--compact' : '',
    horizontal ? 'language-dropdown--horizontal' : '',
    showFlags ? 'language-dropdown--with-flags' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div ref={dropdownRef} className={dropdownClasses}>
      <button
        className="language-dropdown__trigger interactive-hover transition-standard"
        onClick={handleToggle}
        onKeyDown={(e) => handleKeyPress(e)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Current language: ${selectedLanguage.label}. Click to open language menu.`}
        disabled={isChangingLanguage}
      >
{/* Flag temporarily disabled due to broken image 
        {showFlags && selectedLanguage.flag && (
          <img 
            src={selectedLanguage.flag} 
            alt={`${selectedLanguage.label} flag`}
            className="language-dropdown__flag"
          />
        )}
        */}
        <span 
          className="language-dropdown__text" 
          style={variant === 'transparent' ? { color: 'var(--color-awasi)' } : undefined}
        >
          {selectedLanguage.code}
        </span>
        <div className={`language-dropdown__arrow ${isOpen ? 'language-dropdown__arrow--open' : ''}`}>
          <img src={variant === 'light' || variant === 'transparent' ? images.navDropdownArrowDark : images.navDropdownArrow} alt="Dropdown arrow" />
        </div>
      </button>

      <div className={`language-dropdown__menu ${isOpen ? 'language-dropdown__menu--open' : ''}`}>
        <ul 
          className="language-dropdown__list"
          role="listbox"
          aria-label="Language options"
        >
          {languages.map((language) => (
            <li
              key={language.code}
              className={`language-dropdown__item ${
                selectedLanguage.code === language.code ? 'language-dropdown__item--selected' : ''
              }`}
              role="option"
              aria-selected={selectedLanguage.code === language.code}
              tabIndex={isOpen ? 0 : -1}
              onClick={() => handleLanguageSelect(language)}
              onKeyDown={(e) => handleKeyPress(e, language)}
            >
              <div className="language-dropdown__item-content">
                {/* Flag temporarily disabled due to broken image 
                {showFlags && language.flag && (
                  <img 
                    src={language.flag} 
                    alt={`${language.label} flag`}
                    className="language-dropdown__item-flag"
                  />
                )}
                */}
                <div className="language-dropdown__item-text">
                  <span className="language-dropdown__item-code">
                    {language.code}
                  </span>
                  {!compact && (
                    <span className="language-dropdown__item-label">
                      {t(`languages:${language.code.toLowerCase()}`)}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LanguageDropdown;